"use client";

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock, BarChart3, Play, Search, Loader2 } from "lucide-react";
import { getResources, type ResourceItem } from "@/lib/resources";
import { usePortal } from "@/hooks/use-portal";

export default function TestSeriesPage() {
  const [searchParams] = useSearchParams();
  const { portal, setPortal } = usePortal();
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Sync Portal from URL
  useEffect(() => {
    const filter = searchParams.get("filter")?.toLowerCase();
    if (filter === "tspsc") {
      setPortal("tspsc");
    } else if (filter === "appsc") {
      setPortal("appsc");
    }
  }, [searchParams, setPortal]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getResources("test_series", true);
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    
    return items.filter((i: any) => {
      // 0. Visibility check
      if (i.isVisible === false) return false;

      // 1. Portal filtering
      const iPortal = (i.portal || "").toLowerCase();
      
      if (portal === "tspsc" && (iPortal === "appc" || iPortal === "appsc")) return false;
      if (portal === "appsc" && (iPortal === "tspc" || iPortal === "tspsc")) return false;

      // 2. Search query filtering
      const title = (i.title || "").toLowerCase();
      const desc = (i.description || "").toLowerCase();

      // EXCLUDE UPSC
      if (title.includes("upsc")) return false;

      if (!q) return true;
      return title.includes(q) || desc.includes(q);
    });
  }, [items, searchQuery, portal]);

  return (
    <div className="min-h-screen bg-bg-secondary/30">
      <section className="bg-white border-b border-border pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-text-primary mb-6 font-display">
            {portal === "tspsc" ? "TSPSC" : portal === "appsc" ? "APPSC" : "Online"} <span className="text-primary ">Test Series</span>
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-10">Practice sets managed from the admin dashboard.</p>

          <div className="max-w-xl mx-auto relative group">
            <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none text-text-muted group-focus-within:text-primary transition-colors">
              <Search size={22} />
            </div>
            <input
              type="text"
              placeholder="Search test series..."
              className="w-full h-16 pl-14 pr-6 rounded-2xl bg-white border-2 border-border focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all text-lg font-medium shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="animate-spin text-primary" size={36} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-10 text-center text-text-secondary">
            No test series found.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filtered.map((test) => (
            <div
              key={test.id}
              className="group bg-white rounded-3xl border border-border p-8 hover:border-primary hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 flex flex-col relative"
            >
              <div className="space-y-3 mb-6">
                <h3 className="text-2xl font-black text-text-primary leading-tight group-hover:text-primary transition-colors">
                  {test.title?.replace("TPPSC", "TSPSC")}
                </h3>
              </div>

              <div className="grid grid-cols-3 gap-6 mb-8 py-6 border-y border-slate-50">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-text-muted uppercase tracking-widest">Duration</p>
                  <div className="flex items-center gap-2 font-bold text-text-secondary">
                    <Clock size={16} className="text-primary" />
                    <span>—</span>
                  </div>
                </div>
                <div className="space-y-1 text-center">
                  <p className="text-[10px] font-black text-text-muted uppercase tracking-widest">Questions</p>
                  <div className="flex items-center justify-center gap-2 font-bold text-text-secondary">
                    <BarChart3 size={16} className="text-primary" />
                    <span>—</span>
                  </div>
                </div>
                <div className="space-y-1 text-right">
                  <p className="text-[10px] font-black text-text-muted uppercase tracking-widest">Price</p>
                  <div className="font-bold text-text-primary text-lg">₹—</div>
                </div>
              </div>

              <Link
                to={`/test-series/${test.slug}`}
                className="w-full h-14 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-lg shadow-2xl shadow-slate-200 transition-all flex items-center justify-center gap-3 active:scale-95 group"
              >
                <Play size={20} className="fill-current" />
                VIEW DETAILS
              </Link>
            </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

